import { createApi } from '@reduxjs/toolkit/query/react';
import { createBackendBaseQuery, transformApiError } from '../../utils/createAuthBaseQuery';
import { API_URLS } from '../../constants/apiUrl';

export interface SaveInternalInvestigationSearchParametersResponse {
  id: number;
  title: string;
  status: number;
}

export interface VoltageParameters {
  voltageV: number;
  powerKw: number;
  frequencyHz: number;
  minFrequencyHz: number;
  maxFrequencyHz: number;
  serviceFactor: number;
  noOfPoles: number;
}

export interface MechanicalParameter {
  shaftLength: number;
  shaftHeight: number;
  shaftDiameter: number;
  pcd: number;
  shaftToGBDistance: number;
  overallLength: number;
  balancingType: string;
}
// Interface for creating a new user
export interface InternalInvestigationSearchParameters {
  id?: number;
  motorInvestigationTicketId: number;
  isInvestigationInternal: boolean;
  motorRegionId: number;
  motorTypeId: number;
  motorSpeedTypeId: number;
  motorMountingTypeId: number;
  voltageParameterList: VoltageParameters[];
  mechanicalParameter: MechanicalParameter;
}

export interface Alternative {
  id: number;
  motorId: number;
  motorGuid: string;
  motorPartNumber: number;
  region: string;
  voltage: number;
  power: number;
  frequency: number;
  serviceFactor: number;
  mountingType: string;
  closenessLevel: string;
  closenessLevelRank: number;
  isMatching: boolean | null;
}
export interface FetchInternalAlternativesResponse {
  voltage: number;
  alternatives: Alternative[];
}

const BASE_URL = process.env.REACT_APP_MOTER_SERVICE_API_BASE_URL as string;

if (!BASE_URL) {
  throw new Error('REACT_APP_MOTER_SERVICE_API_BASE_URL environment variable is not set');
}

// Create the base query using the reusable factory
const baseQuery = createBackendBaseQuery(BASE_URL);

export const motorInternalSearchApiSlice = createApi({
  reducerPath: 'motorInternalSearchApi',
  baseQuery: baseQuery,
  tagTypes: ['InternalAlternatives', 'InvestigationSearchParameters'],
  endpoints(builders) {
    return {
      saveInternalInvestigationSearchParameters: builders.mutation<SaveInternalInvestigationSearchParametersResponse, { InternalInvestigationSearchParametersData: InternalInvestigationSearchParameters }>({
      query({ InternalInvestigationSearchParametersData }) {
        return {
        url: API_URLS.MOTOR_INVESTIGATION.SAVE_INTERNAL_SEARCH_PARAMETERS,
        method: 'POST',
        body: InternalInvestigationSearchParametersData,
        };
      },
      invalidatesTags: ['InternalAlternatives', 'InvestigationSearchParameters'],
      transformErrorResponse: transformApiError,
      }),
      fetchInternalAlternatives: builders.query<
      FetchInternalAlternativesResponse[],
      number
      >({
      query: (id) => ({
        url: `${API_URLS.MOTOR_INVESTIGATION.SEARCH_INTERNAL_AVAILABLE_ALTERNATIVE}?investigationFlowId=${id}`,
        method: 'GET',
      }),
      providesTags: ['InternalAlternatives'],
      transformErrorResponse: transformApiError,
      }),
      fetchInternalInvestigationSearchParameters: builders.query<
      InternalInvestigationSearchParameters,
      number
      >({
      query: (id) => ({
        url: `${API_URLS.MOTOR_INVESTIGATION.FETCH_INTERNAL_AVAILABLE_ALTERNATIVE}?investigationTicketId=${id}`,
        method: 'GET',
      }),
      providesTags: ['InvestigationSearchParameters'],
      transformErrorResponse: transformApiError,
      }),
    };
  },
});

export const {
  useSaveInternalInvestigationSearchParametersMutation,
  useFetchInternalAlternativesQuery,
  useLazyFetchInternalAlternativesQuery,
  useFetchInternalInvestigationSearchParametersQuery,
} = motorInternalSearchApiSlice;